import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, User, Eye, EyeOff, AlertTriangle, RefreshCw } from 'lucide-react';
import { ADMIN_API_URL, setStoredToken } from './adminAuth';

const LoginForm = ({ onLogin, error }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${ADMIN_API_URL}/v1/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Invalid credentials');
      }
      const data = await response.json();
      setStoredToken(data.access_token);
      onLogin(true);
    } catch (err) {
      onLogin(false, err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "var(--lm-canvas)" }}>
      <div className="rounded-xl p-8 w-full max-w-md" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t("adminLogin.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("adminLogin.subtitle")}</p>
        </div>

        {error && (
          <div className="bg-red-950/20 border border-red-900/40 text-red-400 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t("adminLogin.username")}</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="superadmin"
                required
                className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">{t("adminLogin.password")}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="********"
                required
                className="w-full pl-10 pr-12 py-3 border border-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                aria-label={showPassword ? t("adminLogin.hidePassword") : t("adminLogin.showPassword")}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <><RefreshCw className="w-5 h-5 animate-spin" />{t("adminLogin.signingIn")}</> : <><Lock className="w-5 h-5" />{t("adminLogin.signIn")}</>}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">{t("adminLogin.adminOnly")}</p>
      </div>
    </div>
  );
};

export default LoginForm;
